import { FocusableOption } from "@angular/cdk/a11y";
import {
    booleanAttribute,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    input,
    model,
    numberAttribute,
    Output,
    signal,
    viewChild,
    ViewEncapsulation,
    inject,
    HostAttributeToken
} from "@angular/core";
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from "@angular/forms";

const VUI_CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => VdsCheckbox),
    multi: true
};

export class VdsCheckboxChange {
    source!: VdsCheckbox;
    checked!: boolean;
}

let nextUniqueId = 0;

@Component({
    selector: "vds-checkbox",
    templateUrl: "./checkbox.html",
    styleUrl: "./checkbox.css",
    host: {
        class: "vds-checkbox",
        "[attr.tabindex]": "null",
        "[attr.aria-label]": "null",
        "[attr.aria-labelledby]": "null",
        "[class.vds-checkbox--checked]": "checked()",
        "[class.vds-checkbox--disabled]": "disabled",
        "[class.vds-checkbox--left-align]": "leftAlign()",
        "[id]": "id()"
    },
    providers: [
        VUI_CHECKBOX_CONTROL_VALUE_ACCESSOR,
        {
            provide: NG_VALIDATORS,
            useExisting: VdsCheckbox,
            multi: true
        }
    ],
    exportAs: "vdsCheckbox",
    encapsulation: ViewEncapsulation.None,
    standalone: true
})
export class VdsCheckbox implements ControlValueAccessor, Validator, FocusableOption {
    _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private _changeDetectorRef = inject(ChangeDetectorRef);

    focus() {
        this._inputElement().nativeElement.focus();
    }

    protected _createChangeEvent(isChecked: boolean) {
        const event = new VdsCheckboxChange();
        event.source = this;
        event.checked = isChecked;
        return event;
    }

    private _uniqueId = `vds-checkbox-${++nextUniqueId}`;

    id = input<string>(this._uniqueId);

    inputId = computed(() => `${this.id() || this._uniqueId}-input`);

    required = input<boolean, string>(false, { transform: booleanAttribute });

    @Input({ alias: "checked", transform: booleanAttribute })
    set _checked(value: boolean) {
        if (value !== this.checked()) {
            this.checked.set(value);
        }
    }
    checked = signal<boolean>(false);

    @Input({ transform: booleanAttribute })
    get disabled() {
        return this._disabled();
    }
    set disabled(value: boolean) {
        if (value !== this._disabled()) {
            this._disabled.set(value);
        }
    }
    private _disabled = signal<boolean>(false);

    @Input({ alias: "indeterminate", transform: booleanAttribute })
    set _indeterminate(value: boolean) {
        const changed = value !== this.indeterminate();
        this.indeterminate.set(value);

        if (changed) {
            this.indeterminateChange.emit(this.indeterminate());
        }
    }
    indeterminate = signal<boolean>(false);

    /**
     * Attached to the aria-label attribute of the host element. In most cases, aria-labelledby will
     * take precedence so this may be omitted.
     */
    ariaLabel = input<string | null>("", { alias: "aria-label" });

    /**
     * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
     */
    ariaLabelledBy = input<string | null>(null, { alias: "aria-labelledby" });

    /** The 'aria-describedby' attribute is read after the element's label and field type. */
    ariaDescribedBy = input<string | null>(null, { alias: "aria-describedby" });

    @Input({ alias: "tabIndex", transform: (value: unknown) => (value == null ? undefined : numberAttribute(value)) })
    set _tabIndex(value: number) {
        this.tabIndex.set(value);
    }
    tabIndex = signal<number>(0);

    /** Whether the label should appear after or before the checkbox. Defaults to 'after' */
    labelPosition = input<"before" | "after">("after");

    /** Name value will be applied to the input element if present */
    name = input<string | null>(null);

    /** Whether the checkbox should left align in its container */
    leftAlign = input(false, { transform: booleanAttribute });

    /** The value attribute of the native input element */
    value = model<string>();

    _inputElement = viewChild.required<ElementRef<HTMLInputElement>>("input");

    _labelElement = viewChild.required<ElementRef<HTMLInputElement>>("label");

    /** Event emitted when the checkbox `checked` value changes. */
    @Output() readonly change: EventEmitter<VdsCheckboxChange> = new EventEmitter<VdsCheckboxChange>();

    /** Event emitted when the checkbox `indeterminate` value changes. */
    @Output() readonly indeterminateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
     * @docs-private
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    _onTouched: () => any = () => {};

    constructor() {
        const tabIndex = inject(new HostAttributeToken("tabindex"), { optional: true });

        this.tabIndex.set(parseInt(tabIndex ?? "0", 10) || 0);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _controlValueAccessorChangeFn: (value: any) => void = () => {};

    // Implemented as part of ControlValueAccessor.
    writeValue(value: any) {
        this.checked.set(!!value);
    }

    // Implemented as part of ControlValueAccessor.
    registerOnChange(fn: (value: any) => void) {
        this._controlValueAccessorChangeFn = fn;
    }

    // Implemented as part of ControlValueAccessor.
    registerOnTouched(fn: any) {
        this._onTouched = fn;
    }

    // Implemented as part of ControlValueAccessor.
    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    // Implemented as part of Validator.
    validate(control: AbstractControl<boolean>): ValidationErrors | null {
        return this.required() && control.value !== true ? { required: true } : null;
    }

    private _emitChangeEvent() {
        this._controlValueAccessorChangeFn(this.checked());
        this.change.emit(this._createChangeEvent(this.checked()));

        if (this._inputElement()) {
            this._inputElement().nativeElement.checked = this.checked();
        }
    }

    toggle(): void {
        this.checked.set(!this.checked());
    }

    protected _handleInputClick() {
        const clickAction = "check" as string;

        // If resetIndeterminate is false, and the current state is indeterminate, do nothing on click
        if (!this.disabled && clickAction !== "noop") {
            // When user manually click on the checkbox, `indeterminate` is set to false.
            if (this.indeterminate() && clickAction !== "check") {
                this.indeterminate.set(false);
                this.indeterminateChange.emit(this.indeterminate());
            }

            this.checked.set(!this.checked());

            this._emitChangeEvent();
        } else if (!this.disabled && clickAction === "noop") {
            this._inputElement().nativeElement.checked = this.checked();
            this._inputElement().nativeElement.indeterminate = this.indeterminate();
        }
    }

    _onInteractionEvent(event: Event) {
        event.stopPropagation();
    }

    _onBlur() {
        Promise.resolve().then(() => {
            this._onTouched();
            this._changeDetectorRef.markForCheck();
        });
    }

    _onInputClick() {
        this._handleInputClick();
    }

    _onTouchTargetClick() {
        this._handleInputClick();

        if (!this.disabled) {
            this._inputElement().nativeElement.focus();
        }
    }

    _preventBubblingFromLabel(event: MouseEvent) {
        if (!!event.target && this._labelElement().nativeElement.contains(event.target as HTMLElement)) {
            event.stopPropagation();
        }
    }
}
